import { useState, useEffect } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid";

const useEbook = (columns: GridColDef[]) => {
  const [ebookData, setEbookData] = useState<any[]>([]);
  const [selectedEbookView, setSelectedEbookView] = useState<
    string | null
  >(null);
  const [selectedEbookId, setSelectedEbookId] = useState<string | null>(
    null
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortingModel, setSortingModel] = useState<GridSortModel>();
  // New state for dedicated time sorting
  const [timeSortOrder, setTimeSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid is 0-indexed
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0); // Total number of rows
  const navigate = useNavigate();
  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchData();
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    fetchData();
  }, [paginationModel, filterModel, sortingModel, timeSortOrder]); // Added timeSortOrder to dependencies

  // Effect to reset time sort when grid sort is used
  useEffect(() => {
    if (sortingModel && sortingModel.length > 0) {
      setTimeSortOrder(null);
    }
  }, [sortingModel]);

  const fetchData = async () => {
    try {
      const params: any = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: filterModel?.items.map(({ field, operator, value }) => ({
          field,
          type: columns?.find((col) => col.field === field)?.type || "string",
          operator,
          value,
        })),
        // Prioritize timeSortOrder, otherwise use the grid's sortingModel
        sortBy: timeSortOrder ? "createdAt" : sortingModel?.[0]?.field || "",
        sortOrder: timeSortOrder || sortingModel?.[0]?.sort || undefined,
      };

      // Add search query parameter if provided
      if (searchQuery && searchQuery.trim().length > 0) {
        params.q = searchQuery.trim();
      }

      const result = await ApiService({
        method: "GET",
        endpoint: apiPaths.getbook,
        params
      });
      console.log("Ebook : ", result);

      if (result?.data) {
        setEbookData(result?.data || []);
        setRowCount(result?.totalCount || 0);
      } else {
        setEbookData(result || []);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const formatDateToISODateString = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const handleDateFilter = async (fromDate: Date, toDate: Date) => {
    const formattedFromDate = formatDateToISODateString(fromDate);
    const formattedToDate = formatDateToISODateString(toDate);

    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getbook}?fromDate=${formattedFromDate}&toDate=${formattedToDate}`,
      });
      setEbookData(result);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  };

  const handleView = (id: string) => {
    setSelectedEbookView(id);
  };

  const handleEdit = (id: string) => {
    setSelectedEbookId(id);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.deletebook}/${id}`,
      });
      setEbookData((prevData) =>
        prevData.filter((ebook) => ebook._id !== id)
      );
      toast.success("Ebook deleted successfully");
    } catch (error) {
      console.error("Error deleting ebook:", error);
      toast.error("Error deleting ebook");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUp = async (id: string, currentStatus: boolean) => {
    setLoading(true);
    const newStatus = currentStatus;
    console.log(newStatus);
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updatebook}/${id}`,
        data: { isActive: newStatus },
      });
      setEbookData((prevData) =>
        prevData.map((ebook) =>
          ebook._id === id ? { ...ebook, isActive: newStatus } : ebook
        )
      );
    } catch (error) {
      console.error("Error updating ebook status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/add-ebook");
  };

  // New handler to cycle through time sorting options
  const handleSortByTime = () => {
    // When using this button, clear the grid's own sort model
    setSortingModel([]);
    setTimeSortOrder((prev) => {
      if (prev === "desc") return "asc";
      if (prev === "asc") return null; // Turn off
      return "desc"; // Start with descending
    });
  };

  return {
    selectedEbookView,
    selectedEbookId,
    setSelectedEbookId,
    ebookData,
    loading,
    searchQuery,
    setSearchQuery,
    handleView,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    handleAdd,
    setFilterModel,
    setSortingModel,
    rowCount,
    paginationModel,
    setPaginationModel,
    // Export the new handler and sort order
    handleSortByTime,
    timeSortOrder,
  };
};

export default useEbook;
